// ─────────────────────────────────────────────
//  staffCreation — the one rule about who may bring a colleague into existence.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  THE DEFECT THIS REMOVES. On a name it could not match, the importer created
//  a staff record. Twenty-three duplicate colleagues appeared in two days, one
//  real person exists five times, and the live directory carried records named
//  "Charlotte Sanchez 7a-7a", "Josh Larson 7 REQ", "Dave/Nick" and
//  "Jim Slone 3, Gabi Swanson" — none of whom is a person.
//
//  Product owner, 2026-08-20: **"never create — always ask a human."**
//
//  ⚠️ THE GUARD IS AT THE WRITE, NOT AT THE MENU. A screen that stops offering
//  the wrong button is a copy change; the thing that has to become impossible is
//  the WRITE. So `addStaff` asks this function first, and a caller that labels
//  itself with an import origin is refused whatever the UI did.
//
//  ⚠️ THIS IS THE SECOND OF TWO GUARDS, AND THE WEAKER ONE. The first is
//  structural: the import pipeline no longer produces a list of names to create
//  at all (see parseGlobalSheet's note on the deleted `partitionNewStaff`), and
//  `noBlindCreation.test.js` fails the build if any module on the import path
//  regains a staff write. This one catches the case that guard cannot see — a
//  future caller that reaches `addStaff` from somewhere new while carrying an
//  import's source tag.
//
//  Pure — no Firebase, no React, no network.
// ─────────────────────────────────────────────

/**
 * Source tags that mean "a machine read a spreadsheet". Every one of these is a
 * value the app already writes onto assignment rows and drafts, so a caller that
 * copies a row's `source` onto a staff payload is refused rather than trusted.
 */
export const IMPORT_ORIGINS = new Set([
  "sheet-import",
  "sheet-auto",
  "global-sheet",
  "scheduled",
  "scheduled-internal",
  "ai-import",
  "internal-schedule",
]);

/** The one source a creation MAY carry: a person answering a review item. */
export const HUMAN_REVIEW_SOURCE = "import-review";

/**
 * Refuse a staff creation that came from an import.
 *
 * Throws rather than returning false: this sits in front of a Firestore write,
 * and a caller that ignores a boolean would create the person anyway. The
 * message names the ruling so the next reader does not have to find this file.
 *
 * @param {object} data the staff payload about to be written
 * @throws {Error} when the payload is attributed to an unattended import
 */
export function assertHumanStaffCreation(data = {}) {
  const source = String(data.source ?? "").trim();
  if (IMPORT_ORIGINS.has(source)) {
    throw new Error(
      `Refusing to create a staff record from "${source}". An import may never ` +
      "create a person — an unidentified name is a review item a human answers. " +
      "(Product owner, 2026-08-20: never create, always ask a human.)"
    );
  }
}
